import React from 'react';
import MuiAlert from '@mui/material/Alert';
import MuiAlertTitle from '@mui/material/AlertTitle';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import CloseIcon from '@mui/icons-material/Close';
import {
    bool,
    string,
    oneOfType,
    array,
    func,
    object
} from 'prop-types';
import { styled } from '@mui/material/styles';
import { Enum, museoSansFont500, museoSansFont700, paths } from 'util/EnumHelper';
import breakpointsNames from 'styles/ResponsiveConstants';
import commonUseStyles from 'styles/CommonStyles';
import withRouter from 'util/customHooks/withRouter';
import StyledButton from '../StyledButton/StyledButton';

const SystemAlert = styled(MuiAlert)(({ theme }) => ({
    '& .MuiAlert-message': {
        marginTop: 5,
        fontSize: 14
    },
    backgroundColor: `${theme.palette.everDrivenLightBlue} !important`,
    ...commonUseStyles.defaultResponsive,
    minHeight: 130,
    margin: '8px 53px',
    [theme.breakpoints.down(breakpointsNames.md)]: {
        margin: '8px 0px'
    }
}));

const Title = styled(MuiAlertTitle)(({ theme }) => ({
    color: `${theme.palette.white} !important`,
    fontSize: 24,
    fontFamily: museoSansFont700,
    lineHeight: 'normal',
    display: 'flex',
    alignItems: 'center'
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
    color: theme.palette.white,
    marginLeft: 'auto',
    fontSize: 18
}));

const OverflowText = styled('span')(({ theme }) => ({
    color: `${theme.palette.white} !important`,
    paddingRight: 15,
    fontFamily: museoSansFont500,
    fontSize: 16,
    lineHeight: '24px'
}));

const StyledLink = styled(StyledButton)(({ theme }) => ({
    color: `${theme.palette.white} !important`,
    fontFamily: museoSansFont500,
    fontWeight: '500',
    fontSize: 16,
    textDecoration: 'underline',
    padding: 0,
    marginTop: -2,
    '&:hover': {
        textDecoration: 'underline',
        background: 'transparent'
    }
}));

function StyledSystemAlert({
    isAlert,
    message,
    show,
    title,
    linkText,
    type,
    onClose,
    router
}) {
    const { navigate } = router;

    const goToViewRouteDetailResource = () => {
        navigate(paths.Resources.path,
            { state: { isRouteDetailExpanded: true } }
        );
    };

    const handleCloseBanner = () => {
        onClose(false);
        localStorage.setItem(Enum.closedBannerCookie, "true");
    };

    if (!isAlert) {
        return null;
    }

    return (
        <Collapse in={show}>
            <SystemAlert
                elevation={6}
                severity={type === Enum.severityType.warning
                    ? Enum.severityType.error
                    : Enum.severityType.info
                }
                icon={false}
                variant="filled"
            >
                <Title>
                    <div>
                        {title}
                    </div>
                    <CloseButton
                        aria-label="close"
                        color="inherit"
                        size="small"
                        data-testid="closeTemporalBanner"
                        onClick={handleCloseBanner}
                    >
                        <CloseIcon fontSize="inherit" />
                    </CloseButton>
                </Title>
                <OverflowText>
                    {message} { linkText
                        &&
                        <StyledLink
                            testId="LearnMoreBannerLink"
                            variant="text"
                            onClick={(event) => {
                                event.preventDefault();
                                goToViewRouteDetailResource();
                            }}
                        >
                            {linkText}
                        </StyledLink>
                    }
                </OverflowText>
            </SystemAlert>
        </Collapse>
    );
}

StyledSystemAlert.defaultProps = {
    isAlert: false,
    message: '',
    show: true,
    title: '',
    linkText: '',
    type: Enum.severityType.warning,
    router: {},
    onClose: () => null
};

/* eslint-disable react/forbid-prop-types */
StyledSystemAlert.propTypes = {
    isAlert: bool,
    message: oneOfType([
        string,
        array
    ]),
    show: bool,
    title: string,
    linkText: string,
    type: string,
    router: object,
    onClose: func
};
/* eslint-enable react/forbid-prop-types */

export default withRouter(StyledSystemAlert);
